import { SearchItem } from '@components/cockpit/panels/notificationSearch/filterComponents/searchFilterTabs/SearchTab';
import {
  SearchFilterFieldName,
  SearchFilterOptionEnum,
} from '@components/cockpit/panels/notificationSearch/notificationSearch.types';
import { getBackendField } from '@components/cockpit/panels/notificationSearch/notificationSearch.utils';
import { useNotificationSearchContext } from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';

export type SearchFilterFieldState = {
  inputValueByField: Record<SearchFilterFieldName, string>;
  selectedItemsByField: Record<SearchFilterFieldName, SearchItem[]>;
};

export type SearchFilterFieldAction =
  | {
      type: 'SET_INPUT_VALUE';
      field: SearchFilterFieldName;
      value: string;
    }
  | {
      type: 'SET_SELECTED_ITEMS';
      field: SearchFilterFieldName;
      value: SearchItem[];
    }
  | {
      type: 'SYNC_SELECTED_ITEMS';
      field: SearchFilterFieldName;
      visibleItems: SearchItem[];
      selectedValues: string[];
    }
  | {
      type: 'SELECT_VISIBLE_ITEMS';
      field: SearchFilterFieldName;
      visibleItems: SearchItem[];
    }
  | {
      type: 'DESELECT_VISIBLE_ITEMS';
      field: SearchFilterFieldName;
      visibleItems: SearchItem[];
    }
  | {
      type: 'DESELECT_SELECTED_ITEMS';
      field: SearchFilterFieldName;
    }
  | {
      type: 'RESET';
    };

export const initialSearchFilterFieldState: SearchFilterFieldState = {
  inputValueByField: {
    [SearchFilterOptionEnum.PLATFORM]: '',
    [SearchFilterOptionEnum.COLLABORATIVE_ID]: '',
    [SearchFilterOptionEnum.ID]: '',
    [SearchFilterOptionEnum.ZONES]: '',
    [SearchFilterOptionEnum.TRACK_SENSOR]: '',
  },
  selectedItemsByField: {
    [SearchFilterOptionEnum.PLATFORM]: [],
    [SearchFilterOptionEnum.COLLABORATIVE_ID]: [],
    [SearchFilterOptionEnum.ID]: [],
    [SearchFilterOptionEnum.ZONES]: [],
    [SearchFilterOptionEnum.TRACK_SENSOR]: [],
  },
};

const syncSelection = (
  currentSelected: SearchItem[],
  visibleItems: SearchItem[],
  selectedValues: string[],
): SearchItem[] => {
  const previousHiddenItems = currentSelected.filter(
    (selected) => !visibleItems.some((item) => item.value === selected.value),
  );

  const currentVisibleSelected = visibleItems.filter((item) => selectedValues.includes(item.value));

  return [...previousHiddenItems, ...currentVisibleSelected];
};

const selectVisible = (currentSelected: SearchItem[], visibleItems: SearchItem[]): SearchItem[] => {
  return [
    ...currentSelected.filter((selected) => !visibleItems.some((item) => item.value === selected.value)),
    ...visibleItems,
  ];
};

const deselectVisible = (currentSelected: SearchItem[], visibleItems: SearchItem[]) => {
  return currentSelected.filter((selected) => !visibleItems.some((item) => item.value === selected.value));
};

export function searchFilterFieldReducer(
  state: SearchFilterFieldState,
  action: SearchFilterFieldAction,
): SearchFilterFieldState {
  switch (action.type) {
    case 'SET_INPUT_VALUE':
      return { ...state, inputValueByField: { ...state.inputValueByField, [action.field]: action.value } };
    case 'SET_SELECTED_ITEMS':
      return { ...state, selectedItemsByField: { ...state.selectedItemsByField, [action.field]: action.value } };
    case 'SYNC_SELECTED_ITEMS':
      return {
        ...state,
        selectedItemsByField: {
          ...state.selectedItemsByField,
          [action.field]: syncSelection(
            state.selectedItemsByField[action.field],
            action.visibleItems,
            action.selectedValues,
          ),
        },
      };
    case 'SELECT_VISIBLE_ITEMS':
      return {
        ...state,
        selectedItemsByField: {
          ...state.selectedItemsByField,
          [action.field]: selectVisible(state.selectedItemsByField[action.field], action.visibleItems),
        },
      };
    case 'DESELECT_VISIBLE_ITEMS':
      return {
        ...state,
        selectedItemsByField: {
          ...state.selectedItemsByField,
          [action.field]: deselectVisible(state.selectedItemsByField[action.field], action.visibleItems),
        },
      };
    case 'DESELECT_SELECTED_ITEMS':
      return { ...state, selectedItemsByField: { ...state.selectedItemsByField, [action.field]: [] } };
    case 'RESET':
      return initialSearchFilterFieldState;
    default:
      return state;
  }
}

export const useSearchFilterField = (field: SearchFilterFieldName) => {
  const { setFilters, searchFilterFieldState, setSearchFilterFieldState } = useNotificationSearchContext();
  const selectedItems = searchFilterFieldState.selectedItemsByField[field];

  const setInputValue = (value: string) => {
    setSearchFilterFieldState({ type: 'SET_INPUT_VALUE', field, value });
  };

  const setSelectedItems = (items: SearchItem[]) => {
    setSearchFilterFieldState({ type: 'SET_SELECTED_ITEMS', field, value: items });
    setFilters({ [getBackendField(field)]: items.map((item) => item.value) });
  };

  const syncSelectedItems = (visibleItems: SearchItem[], selectedValues: string[]) => {
    const mergedItems = syncSelection(selectedItems, visibleItems, selectedValues);

    setSearchFilterFieldState({ type: 'SYNC_SELECTED_ITEMS', field, visibleItems, selectedValues });
    setFilters({ [getBackendField(field)]: mergedItems.map((item) => item.value) });
  };

  const selectVisibleItems = (visibleItems: SearchItem[]) => {
    setSearchFilterFieldState({
      type: 'SELECT_VISIBLE_ITEMS',
      field,
      visibleItems,
    });
    setFilters({
      [getBackendField(field)]: selectVisible(selectedItems, visibleItems).map((item) => item.value),
    });
  };

  const deselectVisibleItems = (visibleItems: SearchItem[]) => {
    setSearchFilterFieldState({ type: 'DESELECT_VISIBLE_ITEMS', field, visibleItems });
    setFilters({ [getBackendField(field)]: deselectVisible(selectedItems, visibleItems).map((item) => item.value) });
  };

  const clearSelectedItems = () => {
    setSearchFilterFieldState({ type: 'DESELECT_SELECTED_ITEMS', field });
    setFilters({ [getBackendField(field)]: [] });
  };

  return {
    inputValue: searchFilterFieldState.inputValueByField[field],
    setInputValue,
    selectedItems: searchFilterFieldState.selectedItemsByField[field],
    setSelectedItems,
    syncSelectedItems,
    selectVisibleItems,
    deselectVisibleItems,
    clearSelectedItems,
  };
};
